import { Button, Card, ConfirmDialog, useToast } from '@citizen-walk/shared-ui';
import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import { functions } from '../../lib/firebase';
import type { StreetSurveyPost } from './useStreetSurveyPosts';
import { PlaceholderFeatureModal } from './PlaceholderFeatureModal';
import { deleteStreetSurveyPost } from './streetSurveyPostActions';

interface AnalyzeStreetViewResult {
  ok: boolean;
  postId?: string;
  deduped?: boolean;
  error?: string;
}

export interface StreetSurveyActionBarProps {
  districtId: string;
  selectedPost: StreetSurveyPost | null;
  canRun: boolean;
  onStartAreaSelect: () => void;
  onOpenBatch: () => void;
  /** 削除完了後、親側の selectedPostId をクリアするためのコールバック。 */
  onDeleted: () => void;
  /** 解析・表示している調査 (docs/98 R-97、未指定 = 既定の調査)。 */
  surveyKey?: string;
  /** 詳細レポート・ダッシュボードのリンクに付ける検索文字列 (例 `?survey=bousai`)。 */
  linkQuery?: string;
}

type PlaceholderKind = 'trend' | null;

const PLACEHOLDER_CONTENT: Record<
  Exclude<PlaceholderKind, null>,
  { title: string; message: string }
> = {
  trend: {
    title: '傾向表示（準備中）',
    message: '地点の推移・傾向を可視化する機能は今後追加予定です。現在は未実装です。',
  },
};

export function StreetSurveyActionBar({
  districtId,
  selectedPost,
  canRun,
  onStartAreaSelect,
  onOpenBatch,
  onDeleted,
  surveyKey,
  linkQuery = '',
}: StreetSurveyActionBarProps) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [placeholder, setPlaceholder] = useState<PlaceholderKind>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleReanalyze() {
    if (!selectedPost || !canRun || busy) return;
    setBusy(true);
    try {
      // サーバ側 analyzeStreetView の timeoutSeconds:300 に合わせる (StreetSurveyPage.tsx と同じ理由)。
      const callable = httpsCallable<
        { districtId: string; forceReanalyzePostId: string },
        AnalyzeStreetViewResult
      >(functions, 'analyzeStreetView', { timeout: 300000 });
      const res = await callable({
        districtId,
        forceReanalyzePostId: selectedPost.id,
        ...(surveyKey && surveyKey !== 'default' ? { surveyKey } : {}),
      });
      if (res.data.ok) {
        toast('success', '再解析が完了しました。');
      } else {
        toast('error', `再解析失敗: ${res.data.error ?? '不明なエラー'}`);
      }
    } catch (e) {
      toast('error', `再解析失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  function handleOpenDetail() {
    if (!selectedPost) return;
    window.open(
      `/districts/${districtId}/street-survey/${selectedPost.id}${linkQuery}`,
      '_blank',
      'noopener',
    );
  }

  function handleOpenAreaDashboard() {
    window.open(
      `/districts/${districtId}/street-survey/area-reports${linkQuery}`,
      '_blank',
      'noopener',
    );
  }

  function handleOpenRouteDashboard() {
    window.open(
      `/districts/${districtId}/street-survey/route-reports${linkQuery}`,
      '_blank',
      'noopener',
    );
  }

  async function handleDelete() {
    if (!selectedPost || !canRun) return;
    setDeleting(true);
    try {
      await deleteStreetSurveyPost(districtId, selectedPost.id, [
        selectedPost.photoPath,
        selectedPost.photoThumbPath,
      ]);
      toast('success', '地点を削除しました。');
      onDeleted();
    } catch (e) {
      toast('error', `削除に失敗しました: ${(e as Error).message}`);
    } finally {
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  }

  return (
    <Card padding="md" className="mt-4">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={!selectedPost || !canRun || busy}
          onClick={() => void handleReanalyze()}
        >
          {busy ? '再解析中…' : 'この場所をAI再解析する'}
        </Button>
        <Button variant="secondary" size="sm" onClick={onStartAreaSelect}>
          エリア選択集計
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setPlaceholder('trend')}>
          傾向表示
        </Button>
        <Button variant="secondary" size="sm" onClick={onOpenBatch}>
          一括解析
        </Button>
        <Button variant="secondary" size="sm" disabled={!selectedPost} onClick={handleOpenDetail}>
          詳細レポートを別タブで開く
        </Button>
        <Button variant="secondary" size="sm" onClick={handleOpenAreaDashboard}>
          エリア比較ダッシュボード
        </Button>
        <Button variant="secondary" size="sm" onClick={handleOpenRouteDashboard}>
          路線比較ダッシュボード
        </Button>
        <Button
          variant="danger"
          size="sm"
          disabled={!selectedPost || !canRun || busy || deleting}
          onClick={() => setConfirmDeleteOpen(true)}
        >
          この地点を削除
        </Button>
      </div>
      {placeholder && (
        <PlaceholderFeatureModal
          open
          title={PLACEHOLDER_CONTENT[placeholder].title}
          message={PLACEHOLDER_CONTENT[placeholder].message}
          onClose={() => setPlaceholder(null)}
        />
      )}
      <ConfirmDialog
        open={confirmDeleteOpen}
        intent="danger"
        title="地点を削除しますか？"
        message="この地点の解析結果を完全に削除します。復元できません。"
        confirmLabel={deleting ? '削除中…' : '削除する'}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </Card>
  );
}
